import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { donationService } from '../../services/api';

const NEW_METHOD = { type: 'number', label: '', number: '', holder: '', url: '', color: '#ff7900', zone: 'all' };
const PRESETS = [
  { label: 'Orange Money', color: '#ff7900', type: 'number', zone: 'cm' },
  { label: 'MTN Mobile Money', color: '#ffcc00', type: 'number', zone: 'cm' },
  { label: 'PayPal', color: '#0070ba', type: 'link', zone: 'intl' },
];

function Donations() {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await donationService.get();
      setForm({ ...res.data.campaign, ends_at: res.data.campaign.ends_at || '' });
      setOpen(res.data.open);
    } catch (e) { enqueueSnackbar(e.response?.data?.error || 'Chargement impossible', { variant: 'error' }); }
  }, [enqueueSnackbar]);
  useEffect(() => { load(); }, [load]);

  if (!form) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setMethod = (i, patch) => set('methods', form.methods.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  const save = async (override) => {
    setError('');
    setSaving(true);
    try {
      const body = { ...form, ...override, ends_at: (override?.ends_at ?? form.ends_at) || null };
      const res = await donationService.save(body);
      setForm({ ...res.data.campaign, ends_at: res.data.campaign.ends_at || '' });
      setOpen(res.data.open);
      enqueueSnackbar(res.data.campaign.active ? 'Campagne enregistrée' : 'Campagne désactivée', { variant: 'success' });
    } catch (e) { setError(e.response?.data?.error || 'Enregistrement impossible'); }
    finally { setSaving(false); }
  };

  const status = !form.active ? <Chip label="Boutons masqués" /> : open ? <Chip color="success" label="Campagne en cours : boutons visibles" /> : <Chip color="warning" label="Activée mais terminée ou sans moyen de paiement" />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Campagne de don</Typography>
        {status}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Quand la campagne est active, un bouton « Nous soutenir » apparaît sur le site (barre latérale du dictionnaire et pied de page). Désactivée, il disparaît complètement. Le changement est visible par les visiteurs en moins de 2 minutes.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => set('active', e.target.checked)} />} label={<strong>Campagne active</strong>} />
            <TextField label="Message de la campagne (facultatif)" value={form.message} onChange={(e) => set('message', e.target.value)} inputProps={{ maxLength: 240 }} helperText={`Affiché dans la fenêtre de don, par exemple un objectif. ${form.message.length}/240`} fullWidth multiline />
            <TextField label="Fin automatique (facultatif)" type="date" value={form.ends_at} onChange={(e) => set('ends_at', e.target.value)} InputLabelProps={{ shrink: true }} helperText="Après cette date, les boutons disparaissent tout seuls." sx={{ maxWidth: 260 }} />
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Moyens de paiement</Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {form.methods.map((m, i) => (
          <Card key={i} variant="outlined" sx={{ borderLeft: `6px solid ${m.color || '#999'}` }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField size="small" label="Nom" value={m.label} onChange={(e) => setMethod(i, { label: e.target.value })} inputProps={{ maxLength: 40 }} sx={{ minWidth: 170 }} />
                <TextField size="small" select label="Pour qui ?" value={m.zone || 'all'} onChange={(e) => setMethod(i, { zone: e.target.value })} sx={{ minWidth: 170 }}>
                  <MenuItem value="cm">Depuis le Cameroun</MenuItem>
                  <MenuItem value="intl">Depuis l'étranger</MenuItem>
                  <MenuItem value="all">Tous</MenuItem>
                </TextField>
                <TextField size="small" select label="Type" value={m.type} onChange={(e) => setMethod(i, { type: e.target.value })} sx={{ minWidth: 170 }}>
                  <MenuItem value="number">Numéro de dépôt</MenuItem>
                  <MenuItem value="link">Lien de paiement</MenuItem>
                </TextField>
                {m.type === 'link'
                  ? <TextField size="small" label="Lien (https://…)" value={m.url || ''} onChange={(e) => setMethod(i, { url: e.target.value })} fullWidth />
                  : (<>
                    <TextField size="small" label="Numéro" value={m.number || ''} onChange={(e) => setMethod(i, { number: e.target.value })} placeholder="+237 6XX XX XX XX" />
                    <TextField size="small" label="Titulaire" value={m.holder || ''} onChange={(e) => setMethod(i, { holder: e.target.value })} inputProps={{ maxLength: 60 }} fullWidth />
                  </>)}
                <input type="color" value={m.color || '#2e7d32'} onChange={(e) => setMethod(i, { color: e.target.value })} aria-label="Couleur" style={{ width: 40, height: 32, border: 'none', background: 'none', padding: 0 }} />
                <IconButton onClick={() => set('methods', form.methods.filter((_, j) => j !== i))} aria-label="Retirer"><DeleteIcon /></IconButton>
              </Stack>
            </CardContent>
          </Card>
        ))}
        {form.methods.length === 0 && <Typography variant="body2" color="text.secondary">Aucun moyen de paiement. Ajoutez-en un pour pouvoir activer la campagne.</Typography>}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
        {form.methods.length < 6 && PRESETS.map((p) => (
          <Button key={p.label} size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => set('methods', [...form.methods, { ...NEW_METHOD, ...p }])} sx={{ textTransform: 'none' }}>{p.label}</Button>
        ))}
        {form.methods.length < 6 && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => set('methods', [...form.methods, { ...NEW_METHOD, color: '#2e7d32' }])} sx={{ textTransform: 'none' }}>Autre moyen</Button>}
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={() => save()} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>Enregistrer</Button>
        {form.active && <Button color="warning" onClick={() => save({ active: false })} disabled={saving} sx={{ textTransform: 'none' }}>Désactiver maintenant</Button>}
      </Stack>
    </Box>
  );
}

export default Donations;
